import { Button } from "@component/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@component/card";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { authForgotPassword } from "@service/auth";
import { isValidEmail } from "@util/string";
import { ArrowLeftIcon } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router";

interface LocationState {
    email?: string;
}

export function ForgotPasswordForm() {
    const location = useLocation();
    const locationState = location.state as LocationState;

    const [email, setEmail] = useState(locationState?.email || "");
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState<string>("");

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isValidEmail(email)) {
            setError("Please enter a valid email address.");
            return;
        }

        setIsLoading(true);

        authForgotPassword(email).then((resp) => {
            if (resp) {
                setIsSubmitted(true);
            } else {
                setError("Failed to send the reset link. Please try again.");
            }
        }).catch((error) => {
            setError("Failed to send the reset link. Please try again.");
            setIsSubmitted(false);
        }).finally(() => {
            setIsLoading(false);
        })
    };

    if (isSubmitted) {
        return (
            <Card className="w-full">
                <CardHeader className="space-y-1 text-center">
                    <CardTitle
                        className="text-lg md:text-2xl font-bold"
                    >
                        Check Your Email
                    </CardTitle>
                    <CardDescription>
                        We've sent a password reset link to{" "}
                        <span className="font-medium">{email}</span>
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 text-center">
                    <div className="flex justify-center">
                        <div className="rounded-full bg-green-100 p-3 dark:bg-green-900">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="h-6 w-6 text-green-600 dark:text-green-400"
                            >
                                <rect width="20" height="16" x="2" y="4" rx="2" />

                                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                            </svg>
                        </div>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                        <p style={{ marginTop: "0px", marginBottom: "20px" }}>
                            Please check your email inbox for the link to reset your password.
                        </p>
                        <p>
                            If you don't receive an email within a few minutes, please check
                            your spam folder.
                        </p>
                        <p
                            className="mt-2 text-amber-600 dark:text-amber-400 font-medium"
                            style={{ marginTop: "20px" }}
                        >
                            Note: The reset link will only be valid for 10 minutes.
                        </p>
                    </div>
                </CardContent>
                <CardFooter className="flex flex-col space-y-2">
                    <Button
                        onClick={() => {
                            setIsSubmitted(false);
                            setEmail("")
                        }}
                        variant="outline"
                        className="w-full"
                    >
                        Didn't receive an email? Try again
                    </Button>
                    <Link to="/" className="w-full">
                        <Button variant="ghost" className="w-full">
                            <ArrowLeftIcon className="mr-2 h-4 w-4" />
                            Back to login
                        </Button>
                    </Link>
                </CardFooter>
            </Card>
        );
    }

    return (
        <Card className="w-full">
            <CardHeader className="space-y-1 text-center">
                <CardTitle className="text-lg md:text-2xl font-bold">Forgot Password</CardTitle>
                <CardDescription>
                    Enter your email address and we'll send you a link to reset your
                    password.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                            id="email"
                            type="email"
                            placeholder="you@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                        {error && (
                            <p className="text-sm text-red-500">{error}</p>
                        )}
                    </div>
                    <Button
                        type="submit"
                        variant="default"
                        className="w-full"
                        disabled={isLoading}
                    >
                        {isLoading ? "Sending..." : "Send Reset Link"}
                    </Button>
                </form>
            </CardContent>
            <CardFooter className="text-center">
                <Link
                    to="/"
                    className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center justify-center w-full"
                >
                    <ArrowLeftIcon className="mr-2 h-4 w-4" />
                    Back to login
                </Link>
            </CardFooter>
        </Card>
    )
}